import { sheetLayoutSubsetToSlideLayout } from '../types/learningContentWorksheetCanvas';
import { parseSlideLayoutJson } from '../types/programContentSlideCanvas';
import { slideLayoutToSheetLayoutSubset } from '../types/evaluationPlanSheetCanvas';
import { supabase } from '../supabaseClient';
import { recordDevLocalStorageProgramCacheUse } from '../utils/devNetworkTransferMonitor';
import {
  loadProgramWorksheetRegisteredPages,
  saveProgramWorksheetRegisteredPages,
  type ProgramWorksheetRegisteredPage,
} from '../utils/programWorksheetRegisteredPages';
import {
  normalizeProgramWorksheetRegistryName,
  PROGRAM_WORKSHEET_REGISTERED_PAGE_MAX_COUNT,
  programWorksheetRegistryFallbackName,
} from '../utils/programWorksheetRegistryLimits';
import {
  clearRegistryScopeRows,
  createRegistryItemId,
  dedupeRegistryItemIds,
  insertRegistryScopeRowsWithRetry,
  isRegistryItemId,
} from '../utils/programSlideRegisteredScopeSave';

const REGISTRY_SCOPE_TABLE = 'workspace_worksheet_registered_pages' as const;
const SELECT_COLUMNS = 'id, workspace_id, name, layout_json, order_index, created_at, updated_at';

interface WorkspaceWorksheetRegisteredPageRow {
  id: string;
  workspace_id: string;
  name: string;
  layout_json: unknown;
  order_index: number;
  created_at: string;
  updated_at: string;
}

function rowToPage(row: WorkspaceWorksheetRegisteredPageRow, idx: number): ProgramWorksheetRegisteredPage {
  return {
    id: row.id,
    name: normalizeProgramWorksheetRegistryName(row.name, programWorksheetRegistryFallbackName(idx)),
    layout: slideLayoutToSheetLayoutSubset(parseSlideLayoutJson(row.layout_json)),
    order:
      Number.isFinite(Number(row.order_index)) && Number(row.order_index) >= 0
        ? Number(row.order_index)
        : idx,
    updatedAt: row.updated_at ? new Date(row.updated_at).getTime() : Date.now(),
  };
}

function sortPages(pages: ProgramWorksheetRegisteredPage[]): ProgramWorksheetRegisteredPage[] {
  return [...pages].sort((a, b) => a.order - b.order);
}

async function fetchFromDb(workspaceId: string): Promise<ProgramWorksheetRegisteredPage[]> {
  const { data, error } = await supabase
    .from(REGISTRY_SCOPE_TABLE)
    .select(SELECT_COLUMNS)
    .eq('workspace_id', workspaceId)
    .order('order_index', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  const rows = ((data ?? []) as unknown) as WorkspaceWorksheetRegisteredPageRow[];
  return sortPages(rows.map((row, idx) => rowToPage(row, idx)));
}

export async function getProgramWorksheetRegisteredPages(
  workspaceId: string,
  options?: { forceRefresh?: boolean }
): Promise<ProgramWorksheetRegisteredPage[]> {
  const sid = workspaceId.trim();
  if (!sid) return [];

  if (!options?.forceRefresh) {
    const cached = loadProgramWorksheetRegisteredPages(sid);
    if (cached.length > 0) {
      if (process.env.NODE_ENV === 'development') {
        recordDevLocalStorageProgramCacheUse('활동지레이아웃등록·LS');
      }
      return cached;
    }
  } else {
    try {
      const fromDb = await fetchFromDb(sid);
      saveProgramWorksheetRegisteredPages(sid, fromDb);
      return fromDb;
    } catch {
      const legacyLocal = loadProgramWorksheetRegisteredPages(sid);
      return legacyLocal;
    }
  }

  try {
    const fromDb = await fetchFromDb(sid);
    if (fromDb.length > 0) {
      saveProgramWorksheetRegisteredPages(sid, fromDb);
      return fromDb;
    }
  } catch {
    /* DB 미적용 환경에서는 LS만 사용 */
  }

  const legacyLocal = loadProgramWorksheetRegisteredPages(sid);
  if (legacyLocal.length > 0) return legacyLocal;
  saveProgramWorksheetRegisteredPages(sid, []);
  return [];
}

type NormalizedRegisteredPage = {
  id: string;
  name: string;
  layout: ProgramWorksheetRegisteredPage['layout'];
  order: number;
};

export async function replaceProgramWorksheetRegisteredPages(
  workspaceId: string,
  pages: ProgramWorksheetRegisteredPage[]
): Promise<ProgramWorksheetRegisteredPage[]> {
  const sid = workspaceId.trim();
  if (!sid) return [];

  const now = new Date().toISOString();
  const sliced = pages.slice(0, PROGRAM_WORKSHEET_REGISTERED_PAGE_MAX_COUNT);
  const dedupedIds = dedupeRegistryItemIds(sliced.map((page) => String(page.id ?? '').trim()));
  const normalized: NormalizedRegisteredPage[] = sliced.map((page, idx) => ({
    id: isRegistryItemId(dedupedIds[idx] ?? '') ? dedupedIds[idx]! : createRegistryItemId(),
    name: normalizeProgramWorksheetRegistryName(page.name, programWorksheetRegistryFallbackName(idx)),
    layout: page.layout,
    order: idx,
  }));

  try {
    await clearRegistryScopeRows(REGISTRY_SCOPE_TABLE, sid, '활동지레이아웃');
    if (normalized.length) {
      await insertRegistryScopeRowsWithRetry(
        '활동지레이아웃',
        REGISTRY_SCOPE_TABLE,
        sid,
        normalized,
        async (rows) => {
          const payload = rows.map((page) => ({
            id: page.id,
            workspace_id: sid,
            name: page.name,
            layout_json: sheetLayoutSubsetToSlideLayout(page.layout),
            order_index: page.order,
            created_at: now,
            updated_at: now,
          }));
          return supabase
            .from(REGISTRY_SCOPE_TABLE)
            .insert(payload)
            .select(SELECT_COLUMNS)
            .order('order_index', { ascending: true });
        }
      );
    }
  } catch (error) {
    /** 마이그레이션 전 환경: LS만 저장하고 계속 */
    const message = error instanceof Error ? error.message : String(error);
    if (!/relation|does not exist|schema cache|PGRST/i.test(message)) {
      throw error instanceof Error ? error : new Error(message);
    }
  }

  const saved: ProgramWorksheetRegisteredPage[] = normalized.map((page) => ({
    ...page,
    updatedAt: Date.now(),
  }));
  saveProgramWorksheetRegisteredPages(sid, saved);
  return saved;
}
